import { useState } from 'react';
import styled from '@emotion/styled';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';
import { Typography, Icon, StyledDiv } from '@taleemabad/dsm/components';

// Enums for better type safety and maintainability
enum CaptionLayout {
  DROPDOWN = 'dropdown',
  LABEL = 'label',
}

enum DateFormat {
  ISO = 'YYYY-MM-DD',
  DISPLAY = 'DD MMM YYYY',
}

enum ScreenSize {
  VERY_SMALL = 350,
  SMALL = 480,
  MOBILE = 768,
}

enum YearRange {
  PAST_YEARS = 100,
  FUTURE_YEARS = 100,
}

enum EdgePadding {
  SMALL = 8,
  MEDIUM = 12,
  LARGE = 16,
}

enum Spacing {
  OFFSET = 8,
  MOBILE_PADDING = 32,
  SMALL_PADDING = 30,
  VERY_SMALL_HEIGHT_OFFSET = 16,
  MODAL_PADDING = 24,
  STANDARD_PADDING = 20,
  DESKTOP_HEIGHT_OFFSET = 40,
}

enum SizeConstraints {
  // Heights
  VERY_SMALL_MAX_HEIGHT = 240,
  SMALL_MAX_HEIGHT = 280,
  MOBILE_MAX_HEIGHT = 350,
  DESKTOP_MIN_HEIGHT = 350,
  SCROLL_THRESHOLD = 300,

  // Widths
  VERY_SMALL_MIN_WIDTH = 180,
  VERY_SMALL_MAX_WIDTH = 200,
  SMALL_MAX_WIDTH = 240,
  SMALL_MIN_WIDTH = 220,
  MOBILE_MAX_WIDTH = 320,
  DESKTOP_MAX_WIDTH = 400,

  // Modal widths
  MODAL_VERY_SMALL_WIDTH = 180,
  MODAL_SMALL_WIDTH = 280,
  MODAL_MOBILE_WIDTH = 350,
  MODAL_FALLBACK_WIDTH = 280,
}

enum ZIndex {
  STANDARD = 1000,
  DROPDOWN = 9999,
  MODAL_MAX = 2147483647,
}

enum OverflowBehavior {
  AUTO = 'auto',
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
}

export interface DateTextFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  error?: boolean;
  width?: string;
  supportingText?: string;
  /** Set to true when used inside modals to ensure proper z-index layering */
  inModal?: boolean;
  /** Callback fired when the date picker open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Show clear button when value is present */
  clearable?: boolean;
  'data-cy'?: string;
}

// Helper function to calculate responsive padding
const calculateEdgePadding = (
  isVerySmall: boolean,
  isSmall: boolean,
  isMobile: boolean,
) => {
  if (isVerySmall) return Spacing.MOBILE_PADDING;
  if (isSmall) return Spacing.SMALL_PADDING;
  if (isMobile) return Spacing.MOBILE_PADDING;
  return Spacing.MOBILE_PADDING; // Default for desktop
};

// Helper function to calculate base sizing constraints
const calculateBaseSizing = (
  isVerySmall: boolean,
  isSmall: boolean,
  isMobile: boolean,
  availableHeight: number,
  availableWidth: number,
  edgePadding: number,
) => {
  if (isVerySmall) {
    return {
      maxHeight: Math.min(
        SizeConstraints.VERY_SMALL_MAX_HEIGHT,
        availableHeight - Spacing.VERY_SMALL_HEIGHT_OFFSET,
      ),
      minWidth: `${SizeConstraints.VERY_SMALL_MIN_WIDTH}px`,
      maxWidth: `${Math.min(SizeConstraints.VERY_SMALL_MAX_WIDTH, availableWidth - edgePadding)}px`,
    };
  }

  if (isSmall) {
    return {
      maxHeight: Math.min(
        SizeConstraints.SMALL_MAX_HEIGHT,
        availableHeight - Spacing.STANDARD_PADDING,
      ),
      minWidth: '220px',
      maxWidth: `${Math.min(SizeConstraints.SMALL_MAX_WIDTH, availableWidth - edgePadding)}px`,
    };
  }

  if (isMobile) {
    return {
      maxHeight: Math.min(
        SizeConstraints.MOBILE_MAX_HEIGHT,
        availableHeight - Spacing.STANDARD_PADDING,
      ),
      minWidth: '280px',
      maxWidth: `${Math.min(SizeConstraints.MOBILE_MAX_WIDTH, availableWidth - edgePadding)}px`,
    };
  }

  // Desktop
  return {
    maxHeight: Math.max(
      SizeConstraints.DESKTOP_MIN_HEIGHT,
      availableHeight - Spacing.DESKTOP_HEIGHT_OFFSET,
    ),
    minWidth: '320px',
    maxWidth: `${Math.min(SizeConstraints.DESKTOP_MAX_WIDTH, availableWidth - edgePadding)}px`,
  };
};

// Helper function to format selected date for display
const formatDateForDisplay = (selectedDate: Date | undefined): string => {
  if (!selectedDate || isNaN(selectedDate.getTime())) {
    return '';
  }
  return selectedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Helper function to create date range constraints
const createDateConstraints = (
  minDate: Date | undefined,
  maxDate: Date | undefined,
) => {
  return [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];
};

// Helper function to calculate start/end months for date picker
const calculateDateRange = (
  minDate: Date | undefined,
  maxDate: Date | undefined,
  selectedDate: Date | undefined,
) => {
  const currentYear = new Date().getFullYear();

  // If there's a selected date, use its year as the base, otherwise use current year
  const baseYear = selectedDate?.getFullYear() || currentYear;

  // When we have min/max dates, use them as the boundaries
  // Otherwise, use a wider range for general navigation
  return {
    startMonth: minDate
      ? new Date(minDate.getFullYear(), minDate.getMonth())
      : new Date(baseYear - YearRange.PAST_YEARS, 0),
    endMonth: maxDate
      ? new Date(maxDate.getFullYear(), maxDate.getMonth())
      : new Date(baseYear + YearRange.FUTURE_YEARS, 11),
  };
};

// Helper function to apply modal-specific adjustments
const applyModalAdjustments = (
  sizing: { maxHeight: number; minWidth: string; maxWidth: string },
  isVerySmall: boolean,
  isSmall: boolean,
  isMobile: boolean,
  availableWidth: number,
  edgePadding: number,
) => {
  const modalPadding = Spacing.MODAL_PADDING;
  const totalPadding = edgePadding + modalPadding;

  if (isVerySmall) {
    sizing.maxWidth = `${Math.min(SizeConstraints.MODAL_VERY_SMALL_WIDTH, availableWidth - totalPadding)}px`;
  } else if (isSmall) {
    sizing.maxWidth = `${Math.min(SizeConstraints.SMALL_MIN_WIDTH, availableWidth - totalPadding)}px`;
  } else if (isMobile) {
    sizing.maxWidth = `${Math.min(SizeConstraints.MODAL_SMALL_WIDTH, availableWidth - totalPadding)}px`;
  } else {
    sizing.maxWidth = `${Math.min(SizeConstraints.MODAL_MOBILE_WIDTH, availableWidth - totalPadding)}px`;
  }

  sizing.minWidth =
    Math.min(
      SizeConstraints.MODAL_FALLBACK_WIDTH,
      availableWidth - totalPadding,
    ) + 'px';
  return sizing;
};

// Helper function to render field content
const renderFieldContent = (
  showFloatingLabel: boolean,
  value: string,
  placeholder: string,
  formattedValue: string,
) => {
  if (!showFloatingLabel && !value && placeholder) {
    return (
      <Typography
        variant="textBodyLarge"
        color="var(--md-sys-color-outline-variant)"
      >
        {placeholder}
      </Typography>
    );
  }

  if (value) {
    return (
      <Typography
        variant="textBodyLarge"
        color="var(--md-sys-color-on-surface)"
      >
        {formattedValue}
      </Typography>
    );
  }

  return null;
};

// Helper function to create event handlers
const createEventHandlers = (
  onChange: (value: string) => void,
  handleOpenChange: (open: boolean) => void,
) => {
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const isoString = `${year}-${month}-${day}`;
      onChange(isoString);
    }
    handleOpenChange(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleOpenChange(false);
    }
  };

  const handleWheelScroll = (event: React.WheelEvent) => {
    event.stopPropagation();
  };

  return { handleDateSelect, handleClear, handleKeyDown, handleWheelScroll };
};

export const DateTextField = ({
  label,
  placeholder,
  value,
  onChange,
  required = false,
  disabled = false,
  min,
  max,
  error = false,
  width,
  supportingText,
  inModal = false,
  onOpenChange,
  clearable = false,
  'data-cy': dataCy,
}: DateTextFieldProps) => {
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Wrapper function to handle both internal and external state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  // Floating UI setup - adjust placement and sizing for modals
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement: inModal ? 'bottom' : 'bottom-start', // Center for modals, left-aligned for desktop
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(Spacing.OFFSET),
      flip({
        fallbackPlacements: inModal
          ? ['top', 'bottom', 'left', 'right'] // More fallback options for modals
          : ['top-start', 'bottom-start'],
        padding: inModal ? EdgePadding.LARGE : Spacing.STANDARD_PADDING, // More padding for modals
      }),
      shift({
        padding: EdgePadding.LARGE, // Consistent padding for all contexts
        mainAxis: true,
        crossAxis: true,
      }),
      size({
        apply({ availableHeight, availableWidth, elements }) {
          // Screen size detection
          const isVerySmall = availableWidth < ScreenSize.VERY_SMALL;
          const isSmall = availableWidth < ScreenSize.SMALL;
          const isMobile = availableWidth < ScreenSize.MOBILE;

          // Calculate responsive padding and sizing
          const edgePadding = calculateEdgePadding(
            isVerySmall,
            isSmall,
            isMobile,
          );
          let sizing = calculateBaseSizing(
            isVerySmall,
            isSmall,
            isMobile,
            availableHeight,
            availableWidth,
            edgePadding,
          );

          // Apply modal-specific adjustments if needed
          if (inModal) {
            sizing = applyModalAdjustments(
              sizing,
              isVerySmall,
              isSmall,
              isMobile,
              availableWidth,
              edgePadding,
            );
          }

          // Apply styles to floating element
          Object.assign(elements.floating.style, {
            maxHeight: `${sizing.maxHeight}px`,
            width: 'max-content',
            minWidth: sizing.minWidth,
            maxWidth: sizing.maxWidth,
            overflowY:
              sizing.maxHeight < SizeConstraints.SCROLL_THRESHOLD
                ? OverflowBehavior.AUTO
                : OverflowBehavior.VISIBLE,
            overflowX: inModal
              ? OverflowBehavior.AUTO
              : OverflowBehavior.HIDDEN,
          });
        },
      }),
    ],
  });

  // Floating UI interactions
  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const showFloatingLabel = focused || isOpen || (value && value.trim() !== '');

  // Parse dates and format display value
  const selectedDate = value ? new Date(value) : undefined;
  const formattedValue = formatDateForDisplay(selectedDate);
  const minDate = min ? new Date(min) : undefined;
  const maxDate = max ? new Date(max) : undefined;

  // Create event handlers
  const { handleDateSelect, handleClear, handleKeyDown, handleWheelScroll } =
    createEventHandlers(onChange, handleOpenChange);

  // Calculate date picker constraints
  const dateConstraints = createDateConstraints(minDate, maxDate);
  const { startMonth, endMonth } = calculateDateRange(
    minDate,
    maxDate,
    selectedDate,
  );

  const DatePickerComponent = (
    <StyledDatePickerContainer
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        pointerEvents: 'auto',
      }}
      onWheel={handleWheelScroll}
      $inModal={inModal}
      {...getFloatingProps()}
    >
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        disabled={dateConstraints}
        showOutsideDays
        fixedWeeks
        captionLayout={CaptionLayout.DROPDOWN}
        startMonth={startMonth}
        endMonth={endMonth}
        defaultMonth={selectedDate || new Date()}
      />
    </StyledDatePickerContainer>
  );

  return (
    <StyledDiv
      flexDirection="column"
      width={width ?? '100%'}
      position="relative"
    >
      <StyledField
        type="button"
        error={error}
        disabled={disabled}
        ref={refs.setReference}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`${label}${required ? ' (required)' : ''}`}
        data-date-picker-trigger="true"
        data-cy={dataCy}
        {...getReferenceProps()}
      >
        <StyledLabel
          floating={!!showFloatingLabel}
          error={error}
          disabled={disabled}
        >
          <Typography variant="textBodyLarge">
            {label}
            {required && (
              <span style={{ color: 'var(--md-sys-color-error)' }}> *</span>
            )}
          </Typography>
        </StyledLabel>
        <StyledDiv textAlign="left">
          {renderFieldContent(
            !!showFloatingLabel,
            value,
            placeholder,
            formattedValue,
          )}
        </StyledDiv>
        <StyledIconContainer>
          {clearable && value && (
            <StyledClearButton
              onClick={handleClear}
              type="button"
              aria-label="Clear date"
            >
              <Icon
                name="close"
                size="tiny"
                color="var(--md-sys-color-outline-variant)"
              />
            </StyledClearButton>
          )}
          <Icon
            name="calendar_today"
            size="tiny"
            color="var(--md-sys-color-outline-variant)"
          />
        </StyledIconContainer>
      </StyledField>

      {/* Render date picker */}
      {isOpen &&
        (inModal ? (
          DatePickerComponent
        ) : (
          <FloatingPortal>{DatePickerComponent}</FloatingPortal>
        ))}

      {supportingText && (
        <StyledErrorText>
          <Typography variant={error ? 'textBodyMediumError' : 'textBodySmall'}>
            {supportingText}
          </Typography>
        </StyledErrorText>
      )}
    </StyledDiv>
  );
};

const StyledField = styled.button<{
  error?: boolean;
  disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--rk-spacing-md);
  min-height: 56px;
  border: 1px solid
    ${({ error }) =>
      error
        ? 'var(--md-sys-color-error)'
        : 'var(--md-sys-color-outline-variant)'};
  border-radius: var(--md-sys-shape-corner-sm);
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: border-color 0.2s ease;
  position: relative;

  &:focus {
    border-color: var(--md-sys-color-primary);
  }

  &:hover:not(:disabled) {
    border-color: var(--md-sys-color-on-surface);
  }
`;

const StyledLabel = styled.label<{
  floating: boolean;
  error?: boolean;
  disabled?: boolean;
}>`
  position: absolute;
  left: var(--rk-spacing-md);
  top: ${({ floating }) => (floating ? '-12px' : '50%')};
  font-size: ${({ floating }) => (floating ? '0.75rem' : '1rem')};
  pointer-events: none;
  background: var(--md-sys-color-surface-container-low);
  padding: 0 6px;
  transform: translateY(${({ floating }) => (floating ? '0' : '-50%')});
  transition: all 0.2s;
  z-index: 10;
  color: ${({ error }) =>
    error ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-outline)'};

  /* Ensure proper stacking on mobile devices */
  isolation: isolate;
`;

const StyledDatePickerContainer = styled.div<{
  $inModal?: boolean;
}>`
  background-color: #ffffff;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-sys-shape-corner-md);
  box-shadow: var(--md-sys-elevation-level3);
  /* Remove fixed max-height to let Floating UI handle it dynamically */
  overflow-y: ${OverflowBehavior.VISIBLE}; /* Default to visible, Floating UI will set auto when needed */
  overflow-x: ${({ $inModal }) =>
    $inModal
      ? OverflowBehavior.AUTO
      : OverflowBehavior.HIDDEN}; /* Horizontal scroll only for modals as backup */
  z-index: ${({ $inModal }) => ($inModal ? ZIndex.MODAL_MAX : ZIndex.STANDARD)};
  position: relative;
  scroll-behavior: smooth;

  /* Enhanced modal support */
  ${({ $inModal }) =>
    $inModal &&
    `
    max-width: calc(100vw - 32px); /* Prevent modal overflow */
    min-width: auto; /* Allow shrinking in modals */
  `}

  /* Force solid background and remove any transparency */
  background-image: none !important;
  opacity: 1 !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;

  /* Enable smooth scrolling on mobile as backup */
  -webkit-overflow-scrolling: touch;

  /* Simple scrollbar styling for modal backup */
  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--md-sys-color-surface-variant);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--md-sys-color-outline-variant);
    border-radius: 2px;
  }

  /* Responsive styling for all screen sizes */

  /* Very small screens (< 350px) - ultra compact for all contexts */
  @media (max-width: 349px) {
    .rdp {
      padding: 4px !important; /* Minimal padding */
      min-width: 180px !important; /* Reduced from 200px */
    }

    .rdp-root {
      --rdp-day-height: 26px !important; /* Reduced from 30px */
      --rdp-day_button-height: 24px !important; /* Reduced from 28px */
      --rdp-day_button-width: 24px !important; /* Reduced from 28px */
      --rdp-nav-height: 32px !important; /* Reduced from 36px */
    }

    .rdp-month_caption {
      min-height: 32px !important; /* Reduced from 36px */
      margin-bottom: 2px !important; /* Minimal margin */
      padding: 0 4px !important; /* Minimal padding */
    }

    .rdp-caption_label {
      font-size: 0.75rem !important; /* Slightly larger for readability */
    }

    /* Compact dropdown styling for very small screens */
    .rdp-dropdown_month,
    .rdp-dropdown_year {
      font-size: 0.7rem !important;
      padding: 2px 4px !important;
      margin: 0 2px !important;
    }

    /* Add indentation for very small screens */
    .rdp-months_dropdown,
    .rdp-years_dropdown {
      text-indent: 4px !important; /* Smaller indentation for very small screens */
      padding-left: 2px !important;
      padding-right: 16px !important;
    }

    .rdp-caption_dropdowns {
      gap: 2px !important;
    }

    .rdp-weekday {
      font-size: 0.55rem !important; /* Reduced from 0.6rem */
      padding: 1px 0 !important; /* Minimal padding */
    }

    .rdp-day_button {
      font-size: 0.65rem !important; /* Reduced from 0.7rem */
      width: 24px !important; /* Reduced from 28px */
      height: 24px !important; /* Reduced from 28px */
      padding: 0 !important; /* Remove any padding */
    }

    .rdp-button_previous,
    .rdp-button_next {
      width: 20px !important; /* Reduced from 24px */
      height: 20px !important; /* Reduced from 24px */
    }

    /* Reduce spacing between elements */
    .rdp-weekdays {
      margin-bottom: 2px !important;
    }

    .rdp-week {
      margin-bottom: 1px !important;
    }
  } /* Small screens (350px - 480px) - compact for all contexts */
  @media (min-width: 350px) and (max-width: 479px) {
    .rdp {
      padding: var(--rk-spacing-sm) !important;
      min-width: 240px !important;
    }

    .rdp-root {
      --rdp-day-height: 34px !important;
      --rdp-day_button-height: 30px !important;
      --rdp-day_button-width: 30px !important;
      --rdp-nav-height: 38px !important;
    }

    .rdp-month_caption {
      min-height: 38px !important;
      margin-bottom: var(--rk-spacing-xs) !important;
      padding: 0 var(--rk-spacing-xs) !important;
    }

    .rdp-caption_label {
      font-size: 0.85rem !important;
    }

    /* Compact dropdown styling for small screens */
    .rdp-dropdown_month,
    .rdp-dropdown_year {
      font-size: 0.75rem !important;
      padding: 3px 6px !important;
      margin: 0 3px !important;
    }

    /* Add indentation for small screens */
    .rdp-months_dropdown,
    .rdp-years_dropdown {
      text-indent: 6px !important; /* Medium indentation for small screens */
      padding-left: 3px !important;
      padding-right: 18px !important;
    }

    .rdp-caption_dropdowns {
      gap: 3px !important;
    }

    .rdp-weekday {
      font-size: 0.65rem !important;
      padding: var(--rk-spacing-xs) 0 !important;
    }

    .rdp-day_button {
      font-size: 0.75rem !important;
      width: 30px !important;
      height: 30px !important;
    }

    .rdp-button_previous,
    .rdp-button_next {
      width: 26px !important;
      height: 26px !important;
    }
  }

  /* Modal-specific ultra-compact styling for all screen sizes */
  ${({ $inModal }) =>
    $inModal &&
    `
    /* Base modal styling - ultra compact */
    .rdp {
      padding: var(--rk-spacing-xs) !important; /* Minimal padding for modals */
      min-width: 240px !important; /* Smaller minimum width */
    }

    .rdp-root {
      --rdp-day-height: 28px !important; /* Reduced from default 44px */
      --rdp-day_button-height: 26px !important; /* Reduced from 40px */
      --rdp-day_button-width: 26px !important; /* Reduced from 40px */
      --rdp-nav-height: 32px !important; /* Reduced from 48px */
    }

    .rdp-month_caption {
      min-height: 32px !important; /* Reduced from 48px */
      margin-bottom: var(--rk-spacing-xs) !important; /* Less margin */
      padding: 0 var(--rk-spacing-xs) !important; /* Less padding */
      font-size: 0.875rem !important; /* Smaller font */
    }

    .rdp-caption_label {
      font-size: 0.875rem !important; /* Smaller caption */
    }

    /* Compact weekday headers */
    .rdp-weekday {
      font-size: 0.6rem !important; /* Very small weekday labels */
      padding: 2px 0 !important; /* Minimal padding */
    }

    /* Compact day buttons */
    .rdp-day_button {
      font-size: 0.7rem !important; /* Smaller day numbers */
      width: 26px !important;
      height: 26px !important;
      border-radius: 2px !important; /* Smaller border radius */
    }

    /* Compact navigation buttons */
    .rdp-button_previous,
    .rdp-button_next {
      width: 24px !important;
      height: 24px !important;
      padding: 2px !important;
    }

    /* Reduce spacing between elements */
    .rdp-weekdays {
      margin-bottom: 2px !important;
    }

    .rdp-week {
      margin-bottom: 1px !important;
    }

    /* Compact dropdown styling for modals */
    .rdp-dropdown_month,
    .rdp-dropdown_year {
      font-size: 0.65rem !important;
      padding: 1px 3px !important;
      margin: 0 1px !important;
      max-width: 60px !important;
      border-radius: 2px !important;
    }

    .rdp-dropdown_month {
      max-width: 70px !important;
    }

    .rdp-dropdown_year {
      max-width: 50px !important;
    }

    .rdp-caption_dropdowns {
      gap: 2px !important;
    }
  `}

  /* Force font family inheritance with higher specificity */
  * {
    font-family: var(--md-ref-typeface-brand) !important;
  }

  .rdp {
    margin: 0;
    font-family: var(--md-ref-typeface-brand) !important;
    padding: var(--rk-spacing-md);
  }

  .rdp-root {
    --rdp-day-height: 44px;
    --rdp-day_button-height: 40px;
    --rdp-day_button-width: 40px;
    --rdp-nav-height: 48px;
    --rdp-weekday-padding: var(--rk-spacing-sm);
    font-family: var(--md-ref-typeface-brand) !important;
  }

  .rdp-month_caption {
    color: var(--md-sys-color-on-surface);
    font-weight: 500;
    font-family: var(--md-ref-typeface-brand) !important;
    margin-bottom: var(--rk-spacing-md);
    padding: 0 var(--rk-spacing-md);
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
  }

  .rdp-caption_label {
    font-family: var(--md-ref-typeface-brand) !important;
    font-size: 1rem;
    font-weight: 500;
  }

  /* Dropdown styling for month/year selectors */
  .rdp-dropdown_month,
  .rdp-dropdown_year {
    background: var(--md-sys-color-surface-container-low);
    border: 1px solid var(--md-sys-color-outline-variant);
    border-radius: var(--md-sys-shape-corner-sm);
    color: var(--md-sys-color-on-surface);
    font-family: var(--md-ref-typeface-brand) !important;
    font-size: 0.875rem;
    padding: var(--rk-spacing-xs) var(--rk-spacing-sm);
    margin: 0 var(--rk-spacing-xs);
    cursor: pointer;
    transition: border-color 0.2s ease;

    /* Ensure dropdowns stay within viewport bounds */
    position: relative;
    z-index: ${ZIndex.DROPDOWN};

    &:hover {
      border-color: var(--md-sys-color-on-surface);
    }

    &:focus {
      outline: 2px solid var(--md-sys-color-primary);
      outline-offset: 1px;
      border-color: var(--md-sys-color-primary);
    }
  }

  /* Specific styling for year dropdown to control height and width */
  .rdp-dropdown_year {
    min-width: 80px !important; /* Make year dropdown wider */
    max-height: 120px !important; /* Limit dropdown height */
    overflow-y: ${OverflowBehavior.AUTO} !important; /* Enable scrolling for long lists */

    /* Custom scrollbar for year dropdown */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: var(--md-sys-color-surface-variant);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--md-sys-color-outline-variant);
      border-radius: 2px;

      &:hover {
        background: var(--md-sys-color-outline);
      }
    }
  }

  /* Month dropdown styling */
  .rdp-dropdown_month {
    min-width: 100px !important; /* Make month dropdown appropriately wide */
  }

  /* Add proper indentation for dropdown options */
  .rdp-months_dropdown,
  .rdp-years_dropdown {
    text-indent: 8px !important; /* Add indentation from left edge */
    padding-left: 4px !important; /* Additional left padding */
    padding-right: 20px !important; /* Right padding for dropdown arrow */

    /* Apply accent color for modern browsers */
    accent-color: var(--md-sys-color-primary) !important;
    color-scheme: light !important;
  }

  /* Target the actual select elements with correct class combination */
  select.rdp-dropdown.rdp-months_dropdown,
  select.rdp-dropdown.rdp-years_dropdown {
    accent-color: var(--md-sys-color-primary) !important;
    color-scheme: light !important;
  }

  /* Style dropdown options with correct selectors */
  select.rdp-dropdown.rdp-months_dropdown option,
  select.rdp-dropdown.rdp-years_dropdown option {
    padding-left: 8px !important;
    text-indent: 0 !important;

    /* Default styling */
    background-color: var(--md-sys-color-surface-container) !important;
    color: black !important;

    /* Selected and hover states with primary blue */
    &:checked,
    &:hover,
    &:focus {
      background-color: var(--md-sys-color-primary-container) !important;
      color: var(--md-sys-color-on-primary-container) !important;
      background: var(--md-sys-color-primary-container) !important;
    }
  }

  .rdp-dropdown {
    display: flex;
    align-items: center;
    gap: var(--rk-spacing-xs);
  }

  .rdp-caption_dropdowns {
    display: flex;
    align-items: center;
    gap: var(--rk-spacing-xs);
  }

  .rdp-weekdays {
    margin-bottom: var(--rk-spacing-xs);
  }

  .rdp-weekday {
    color: var(--md-sys-color-outline);
    font-size: 0.75rem;
    font-weight: 500;
    font-family: var(--md-ref-typeface-brand) !important;
    padding: var(--rk-spacing-xs);
    text-align: center;
  }

  .rdp-day_button {
    border: none;
    background: none;
    border-radius: var(--md-sys-shape-corner-sm);
    color: var(--md-sys-color-on-surface);
    font-size: 0.875rem;
    font-family: var(--md-ref-typeface-brand) !important;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--md-sys-color-surface-variant);
    }

    &:focus {
      outline: 2px solid var(--md-sys-color-primary);
      outline-offset: 2px;
    }
  }

  .rdp-day_button[aria-selected='true'] {
    background: var(--md-sys-color-primary);
    color: var(--md-sys-color-on-primary);
  }

  .rdp-day_button:disabled {
    color: var(--md-sys-color-outline);
    cursor: not-allowed;
    opacity: 0.5;
  }

  .rdp-day_button[data-today='true']:not([aria-selected='true']) {
    background: var(--md-sys-color-secondary-container);
    color: var(--md-sys-color-on-secondary-container);
  }

  .rdp-nav {
    display: flex;
    align-items: center;
    gap: var(--rk-spacing-xs);
  }

  .rdp-button_previous,
  .rdp-button_next {
    border: none;
    background: none;
    color: var(--md-sys-color-on-surface);
    cursor: pointer;
    padding: var(--rk-spacing-xs);
    border-radius: var(--md-sys-shape-corner-sm);
    transition: background-color 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: var(--md-sys-color-surface-variant);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .rdp-chevron {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const StyledErrorText = styled.div`
  margin-top: var(--rk-spacing-sm);
`;

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-xs);
`;

const StyledClearButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: var(--md-sys-shape-corner-xs);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--md-sys-color-surface-variant);
  }

  &:focus {
    outline: 2px solid var(--md-sys-color-primary);
    outline-offset: 1px;
  }
`;
